import { Button, Flex, H1, HelpButton, Input, P, Section, Space } from '@dnb/eufemia';
import { useAsync, useEufemiaForm } from '@portals/shared-frontend/hooks';
import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { object, string } from 'zod';

import { deleteServiceDomain, upsertServiceDomain } from '@/api/service-domains';
import BackButton from '@/components/BackButton';
import Container from '@/components/Container';
import DeleteDialog from '@/components/DeleteDialog';
import LoadingModal from '@/components/LoadingModal';
import { useBackend } from '@/hooks/useBackend';

const serviceDomainForm = object({
  name: string().min(1).max(255),
  serviceDomainId: string().min(1).max(20),
});

export function ServiceDomainForm() {
  const { serviceDomainId } = useParams();
  const navigate = useNavigate();

  const { data: serviceDomain } = useBackend(
    '/service-now/service-domains/{serviceDomainId}',
    serviceDomainId ? { params: { path: { serviceDomainId: serviceDomainId } } } : null,
  );

  const {
    controller: { setValues },
    register,
    handleSubmit,
    submitting,
  } = useEufemiaForm(serviceDomainForm, {});

  useEffect(() => {
    if (serviceDomain) {
      setValues(serviceDomain);
    }
  }, [serviceDomain, setValues]);

  const onSubmit = handleSubmit(
    useCallback(
      async (data) => {
        const response = await upsertServiceDomain(serviceDomainId ?? data.serviceDomainId, data);
        if (response.response.ok) {
          navigate('..');
        }
      },
      [serviceDomainId, navigate],
    ),
  );

  const onDelete = useAsync(async () => {
    if (serviceDomainId) {
      const response = await deleteServiceDomain(serviceDomainId);
      if (response.response.ok) {
        navigate('..');
      }
    }
  }, [serviceDomainId, navigate]);

  return (
    <div>
      <BackButton to="/sn-service-domains">Service Domains</BackButton>
      <Container centered>
        {submitting && <LoadingModal />}
        <H1 top="large">{serviceDomainId ? 'Update service domain' : 'Create service domain'}</H1>
        <P>Service domains are defined in Service Now to group services by domain.</P>
        <form onSubmit={onSubmit}>
          <Section backgroundColor="white">
            <Flex.Vertical>
              <Space top="medium">
                <Input
                  label="Service Domain Id"
                  placeholder="BAXXXXX / BSNXXXXXXX"
                  size={40}
                  suffix={<HelpButton size="small">This is the id of the Service Domain.</HelpButton>}
                  {...register.input('serviceDomainId')}
                />
              </Space>
              <Space top="medium">
                <Input
                  label="Name"
                  size={40}
                  suffix={<HelpButton size="small">This is the name of the Service Domain.</HelpButton>}
                  {...register.input('name')}
                />
              </Space>
              <Space top="medium">
                <Button disabled={submitting} icon={serviceDomainId ? 'save' : 'add'} iconPosition="left" type="submit">
                  {serviceDomainId ? 'Save' : 'Create'}
                </Button>
              </Space>
            </Flex.Vertical>
          </Section>
        </form>

        {serviceDomainId && (
          <Section innerSpace={{ bottom: 'large', top: 'large' }} top="medium">
            <DeleteDialog
              loading={onDelete.waiting}
              onDelete={onDelete.execute}
              text="This will permanently delete the service domain. This action cannot be undone."
              title="Are you sure you want to delete this service domain?"
            />
          </Section>
        )}
      </Container>
    </div>
  );
}
